import { api, escapeHtml } from './http';
import { renderProtectedPage } from './layout';
import { createSurveyNoticesPdf, downloadSurveyNoticesPdf } from './notice-pdf';
import type { SurveyCatalogEntry } from '../shared/survey';
import { installPinDialog, pinDialogMarkup } from './pin-gate';

export async function renderGenerateCodesPage(root: HTMLElement): Promise<void> {
  await renderProtectedPage(root, 'Generate Access Codes', async () => {
    const content = root.querySelector<HTMLElement>('#protectedContent')!;
    const [{ surveys }, pinStatus] = await Promise.all([
      api<{ surveys: SurveyCatalogEntry[] }>('/api/surveys'),
      api<{ unlocked: boolean }>('/api/admin/pin'),
    ]);
    let liveDataUnlocked = pinStatus.unlocked;
    content.innerHTML = `
      <section class="card admin-card">
        <p class="section-intro">Create unique seven-character codes for one neighborhood. Codes can be reused by everyone in a household. A PIN is required for non-smoke-test codes.</p>
        <form id="codeForm" class="two-column-form code-form">
          <label>Survey<select name="surveyKey" required>${surveys.map((survey) => `<option value="${survey.key}">${survey.label} (${survey.questionCount} questions)</option>`).join('')}</select></label>
          <label>Neighborhood<input name="neighborhood" maxlength="100" required placeholder="Fairwood"></label>
          <label>Number of codes<input name="quantity" type="number" min="1" max="500" value="25" required></label>
          <label class="checkbox-label"><input id="smokeTestCodes" name="smokeTest" type="checkbox"> Smoke-test codes</label>
          <button id="unlockCodeGeneration" class="secondary-button" type="button"${liveDataUnlocked ? ' hidden' : ''}>Enter PIN</button>
          <button class="primary-button" type="submit"${liveDataUnlocked ? '' : ' disabled'}>Generate codes</button>
        </form>
        <p class="form-message" id="codeMessage" role="alert"></p>
      </section>
      <section id="codeResults" class="card admin-card" hidden></section>
      ${pinDialogMarkup()}`;
    const neighborhoodInput = content.querySelector<HTMLInputElement>('input[name="neighborhood"]')!;
    const smokeTestInput = content.querySelector<HTMLInputElement>('#smokeTestCodes')!;
    const generateButton = content.querySelector<HTMLButtonElement>('#codeForm button[type="submit"]')!;
    const unlockButton = content.querySelector<HTMLButtonElement>('#unlockCodeGeneration')!;
    const updateGate = () => {
      unlockButton.hidden = liveDataUnlocked || smokeTestInput.checked;
      generateButton.disabled = !liveDataUnlocked && !smokeTestInput.checked;
    };
    const openPinDialog = installPinDialog(content, () => {
      liveDataUnlocked = true;
      updateGate();
    });
    unlockButton.addEventListener('click', openPinDialog);
    content.querySelector<HTMLInputElement>('#smokeTestCodes')?.addEventListener('change', (event) => {
      const checked = (event.currentTarget as HTMLInputElement).checked;
      neighborhoodInput.disabled = checked;
      neighborhoodInput.required = !checked;
      updateGate();
    });
    content.querySelector<HTMLFormElement>('#codeForm')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      const data = new FormData(form);
      const message = content.querySelector<HTMLElement>('#codeMessage')!;
      const results = content.querySelector<HTMLElement>('#codeResults')!;
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      button.disabled = true;
      message.textContent = '';
      try {
        const response = await api<{ neighborhood: string; surveyKey: string; surveyId: string; surveyLabel: string; questionCount: number; codes: string[] }>('/api/admin/access-codes/generate', {
          method: 'POST',
          body: JSON.stringify({
            neighborhood: data.get('neighborhood'),
            surveyKey: data.get('surveyKey') || 'original',
            quantity: Number(data.get('quantity')),
            smokeTest: data.get('smokeTest') === 'on',
          }),
        });
        results.hidden = false;
        results.innerHTML = `<div class="result-header"><div><p class="eyebrow">${escapeHtml(response.neighborhood)} · ${escapeHtml(response.surveyLabel)}</p><h2>${response.codes.length} new codes</h2></div><div class="button-row"><button id="copyCodes" class="secondary-button">Copy</button><button id="downloadCodes" class="secondary-button">Download CSV</button><button id="downloadNotices" class="primary-button">Generate survey notices PDF</button></div></div>
          <div class="code-grid">${response.codes.map((code) => `<code>${code}</code>`).join('')}</div>`;
        results.querySelector('#copyCodes')?.addEventListener('click', async () => {
          await navigator.clipboard.writeText(response.codes.join('\n'));
          message.textContent = 'Codes copied.';
        });
        results.querySelector('#downloadCodes')?.addEventListener('click', () => downloadCodes(response.neighborhood, response.surveyKey, response.surveyLabel, response.codes));
        results.querySelector<HTMLButtonElement>('#downloadNotices')?.addEventListener('click', async (noticeEvent) => {
          const noticeButton = noticeEvent.currentTarget as HTMLButtonElement;
          noticeButton.disabled = true;
          noticeButton.textContent = `Building ${response.codes.length}-page PDF…`;
          message.textContent = '';
          try {
            const pdf = await createSurveyNoticesPdf(response.neighborhood, response.codes, response.questionCount);
            downloadSurveyNoticesPdf(response.neighborhood, response.surveyKey, response.surveyLabel, pdf);
            message.textContent = `${response.codes.length} survey notices downloaded.`;
          } catch (error) {
            message.textContent = error instanceof Error ? error.message : 'Survey notices could not be generated.';
          } finally {
            noticeButton.disabled = false;
            noticeButton.textContent = 'Generate survey notices PDF';
          }
        });
      } catch (error) {
        message.textContent = error instanceof Error ? error.message : 'Codes could not be generated.';
      } finally {
        button.disabled = false;
      }
    });
  });
}

function downloadCodes(neighborhood: string, surveyKey: string, surveyLabel: string, codes: string[]): void {
  const rows = ['survey,neighborhood,access_code', ...codes.map((code) => `"${surveyLabel.replaceAll('"', '""')}","${neighborhood.replaceAll('"', '""')}",${code}`)];
  const url = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  const prefix = surveyKey === 'original' ? filenameSlug(neighborhood) : `${filenameSlug(surveyLabel)}-${filenameSlug(neighborhood)}`;
  link.download = `${prefix}-access-codes.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function filenameSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'survey';
}
